import { useToast } from '@repo/design-system/index'
import classNames from 'classnames'
import { useEffect, useRef, useState } from 'react'
import 'react-reflex/styles.css'
import apis from '../../../../modules/apis'
import { axiosPost } from '../../../../modules/axiosHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { useCountdownTimer } from '../../../../modules/hooks/common/useCountdownTimer'
import useIsAdminLogin from '../../../../modules/hooks/common/useIsAdminLogin'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import { s_preparation } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { track } from '../../../../modules/track'
import { IS_PARENT, LOCATION, LOG_CATEGORY, LOG_TYPE, PRE_STUDY } from '../../../../modules/vars'
import { paneMaxHeight } from '../../../../pages/[lang]/portal/lessons/preparation/[lessonId]'
import { TextProvider } from '../../../../pages/[lang]/portal/materials/detail/[materialId]'
import { TAB } from '../../../materials/const'
import AudioPlayer from '../../materials/AudioPlayer'
import { FileUploadBody } from '../../materials/FileUploadBody'
import MaterialInsightBody from '../../materials/MaterialInsightBody'

interface LeftBodyProps {
  material: any
  expressions: any
  isReview: any
  currentTab: any
  setCurrentTab: any
  lesson: any
  materialRef: any
  setTabData: any
}

const LeftBody = ({
  material,
  expressions,
  isReview,
  currentTab,
  setCurrentTab,
  lesson,
  materialRef,
  setTabData,
}: LeftBodyProps) => {
  const { t } = useTranslation()
  const media =
    material?.video?.length > 0 || material?.article?.length > 0
      ? {
          youtubes: material.video,
          articles: material.article,
        }
      : null

  const { toastSuccess } = useToast()
  const isMobile = useIsMobile()

  const handlePauseAudio = () => {
    pause()
  }
  const handlePlayAudio = () => {
    start()
  }
  const handleEndAudio = async () => {
    toastSuccess(t(s_preparation.listenMp3))
  }
  const [mp3Checked, setMp3Checked] = useState(
    lesson?.prestudy?.list.find((item, i) => item.type == PRE_STUDY.MP3)?.value || false
  )
  const audioRef = useRef<HTMLAudioElement>(null)

  const { countdown, start, reset, pause, isRunning } = useCountdownTimer({
    timer: 10000,
    onExpire: () => {
      checkMp3()
    },
  })

  const checkMp3 = async () => {
    if (lesson && !mp3Checked && !IS_PARENT(currentUser)) {
      const payload = {
        action_type: PRE_STUDY.MP3,
      }
      const response = await axiosPost(apis.lessons.preparation.prestudy(lesson?.id), payload)
      if (response) {
        setMp3Checked(true)
      }
    }
  }

  const { currentUser } = useCurrentUser()
  const isAdminLogin = useIsAdminLogin()

  const [audioCurrentTime, setAudioCurrentTime] = useState(0)
  const [isAudioAuto, setIsAudioAuto] = useState(false)

  const [lastDuration, setLastDuration] = useState(null)
  const [audioLogId, setAudioLogId] = useState(null)

  useEffect(() => {
    if (isRunning) {
      let duration = lastDuration || 0
      let log_id = audioLogId
      let playback_time = audioCurrentTime || 0
      duration += 1
      playback_time += 1
      if (duration >= 5 && !isAdminLogin) {
        track({
          log_type: LOG_TYPE.COURSE.MP3_LISTEN,
          log_category: LOG_CATEGORY.LISTEN,
          location: isReview ? LOCATION.REVIEW : LOCATION.PREP,
          lesson_id: lesson?.id,
          duration: duration,
          log_id: log_id,
          playback_time: playback_time,
        }).then((res) => {
          if (!log_id) {
            log_id = res?.log?.id
            setAudioLogId(log_id)
          }
          if ((res?.log?.duration > 10 || duration > 10) && !IS_PARENT(currentUser)) {
            checkMp3()
          }
        })
      }
      setLastDuration(duration)
    }
  }, [isRunning, audioCurrentTime])

  const getDefaultIsPlaying = () => {
    if (media?.youtubes?.length > 0) {
      return media.youtubes.map((item) => false)
    }
    return []
  }
  const getDefaultMediaLogId = () => {
    if (media?.youtubes?.length > 0) {
      return media.youtubes.map((item) => null)
    }
    return []
  }
  const getDefaultMediaPlayback = () => {
    if (media?.youtubes?.length > 0) {
      return media.youtubes.map((item) => null)
    }
    return []
  }
  const getDefaultMediaDuration = () => {
    if (media?.youtubes?.length > 0) {
      return media.youtubes.map((item) => null)
    }
    return []
  }
  const [isPlaying, setIsPlaying] = useState(getDefaultIsPlaying())
  const [mediaLogId, setMediaLogId] = useState(getDefaultMediaLogId())
  const [mediaPlaybackTime, setMediaPlaybackTime] = useState(getDefaultMediaPlayback())
  const [mediaDuration, setMediaDuration] = useState(getDefaultMediaDuration())

  useEffect(() => {
    const tempLogId = [...mediaLogId]
    const tempPlayback = [...mediaPlaybackTime]
    const tempDuration = [...mediaDuration]
    isPlaying.forEach((item, i) => {
      if (!item) {
        const timeout = setTimeout(() => {
          if (!isPlaying[i] && mediaPlaybackTime[i] != null) {
            tempLogId[i] = null
            tempPlayback[i] = null
            tempDuration[i] = null
            setMediaDuration(tempDuration)
            setMediaLogId(tempLogId)
            setMediaPlaybackTime(tempPlayback)
          }
        }, 1000)
        return () => clearTimeout(timeout)
      }
    })
  }, [...isPlaying])

  useEffect(() => {
    const tempLogId = [...mediaLogId]
    const tempDuration = [...mediaDuration]
    const interval = setInterval(() => {
      mediaPlaybackTime.forEach((playBack, i) => {
        const logId = tempLogId[i]
        let duration = tempDuration[i] || 0
        duration += 1
        if (playBack != null) {
          if (duration >= 5) {
            tempDuration[i] = duration
            track({
              location: isReview ? LOCATION.REVIEW : LOCATION.PREP,
              log_type: LOG_TYPE.COURSE.VIDEO,
              log_category: LOG_CATEGORY.LISTEN,
              lesson_id: lesson?.id,
              youtube_id: media.youtubes[i].id,
              log_id: logId,
              duration: duration,
              playback_time: mediaPlaybackTime[i] + duration,
            }).then((res) => {
              if (!logId) {
                tempLogId[i] = res?.log?.id
              }
            })
          }
          tempDuration[i] = duration
        }
      })
      setMediaLogId(tempLogId)
      setMediaDuration(tempDuration)
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [...mediaPlaybackTime])

  useEffect(() => {
    let duration = 1
    let log_id = null
    const interval = setInterval(() => {
      duration += 1
      if (duration >= 5) {
        switch (currentTab) {
          case TAB.INSIGHT:
            track({
              location: isReview ? LOCATION.REVIEW : LOCATION.PREP,
              log_type: LOG_TYPE.COURSE.READ,
              log_category: LOG_CATEGORY.READ,
              lesson_id: lesson?.id,
              duration: duration,
              log_id: log_id,
            }).then((res) => {
              if (!log_id) {
                log_id = res?.log?.id
              }
            })
            break
          case TAB.EXPRESSIONS:
            track({
              location: isReview ? LOCATION.REVIEW : LOCATION.PREP,
              log_type: LOG_TYPE.COURSE.EXPRESSION_STUDY,
              log_category: LOG_CATEGORY.READ,
              log_id: log_id,
              lesson_id: lesson?.id,
              duration: duration,
            }).then((res) => {
              if (!log_id) {
                log_id = res?.log?.id
              }
            })
            break
          default:
            break
        }
      }
    }, 1000)
    return () => clearInterval(interval)
  }, [currentTab])

  return (
    <div
      style={{ maxHeight: currentTab === TAB.UPLOAD_FILE || currentTab === TAB.UPLOAD_LINK ? '' : paneMaxHeight }}
      className={classNames('no-scrollbar mx-[32px] mt-[16px]', [
        currentTab === TAB.UPLOAD_FILE || currentTab === TAB.UPLOAD_LINK ? 'h-full' : '',
      ])}
    >
      {currentTab == TAB.INSIGHT && (
        <>
          <TextProvider>
            <div className="relative">
              <MaterialInsightBody
                materialData={lesson}
                materialId={lesson?.course.id}
                isLesson={isReview}
                setTabData={setTabData}
                isInsightButton={isReview ? false : true}
              />

              <div className="sticky left-0 bottom-0 mx-auto flex flex-row justify-center sm:bottom-[20px]">
                <div className="border-1 min-h-0 w-full rounded-[8px] border-gray-200 sm:flex sm:min-h-[68px] sm:w-[576px] lg:w-[688px] xl:w-[688px]">
                  <AudioPlayer
                    onPlay={() => handlePlayAudio()}
                    onPause={() => handlePauseAudio()}
                    onEnd={handleEndAudio}
                    onClickRange={(time) => console.log(time)}
                    ref={audioRef}
                    src={lesson?.course?.mp3_link}
                    playbacktime={lesson?.course?.playback_time ? lesson?.course?.playback_time : 0}
                    isMobile={isMobile}
                    audioCurrentTime={audioCurrentTime}
                    setAudioCurrentTime={setAudioCurrentTime}
                    isAudioAuto={isAudioAuto}
                    setIsAudioAuto={setIsAudioAuto}
                  />
                </div>
              </div>
            </div>
          </TextProvider>
        </>
      )}

      {/* {currentTab == TAB.INSIGHT && isFreeTalkingOrWriting && <>스피킹 혹은 라이팅</>} */}

      {currentTab === TAB.UPLOAD_FILE && (
        <>
          <FileUploadBody materialRef={materialRef} lesson={lesson} />
        </>
      )}

      {currentTab === TAB.UPLOAD_LINK && (
        <>
          <FileUploadBody materialRef={materialRef} lesson={lesson} />
        </>
      )}
    </div>
  )
}

export default LeftBody
